/* Soulskart side-cart behaviour — remove, quantity, upsell add.

   WHAT THIS REPLACED, AND WHY
   The previous version did two sequential round trips for every change:
     POST /cart/change.js          measured 1606ms
     GET  /?section_id=<header>    measured 1355ms, 46KB
     total                         ~3000ms to remove one line
   The second request re-rendered the ENTIRE header section — header, nav,
   search, mega menu, mobile menu — because the drawer's data-sscart-id is the
   header's section id. It then did a THIRD fetch for the cart icon, using a
   dataset attribute that is usually empty.

   Shopify's cart API renders sections inline. Measured on this store:
     /cart/add.js plain           1441 / 1530ms
     /cart/add.js with sections:  1709ms
   So the render costs about 200ms bundled, against 800-1350ms as its own
   request. Everything below goes through one call. Verified after the rewrite:
   a quantity change fires exactly one request.

   OPTIMISTIC FIRST
   Even at one round trip the wait is over a second, and on a preview theme the
   floor for any request is around 800ms. So the UI moves immediately — the row
   collapses on remove, the number changes on a stepper tap — and the server
   response reconciles. On failure the paint is rolled back.

   WHAT GETS SWAPPED, AND WHY IT IS THE WHOLE COMPONENT
   First version swapped only [data-sscart] plus cart-icon. That left the drawer
   heading stale: "Cart 5" against a server count of 4, because
   .cart-drawer__heading is Horizon's markup and sits OUTSIDE our subtree.
   Swapping the whole cart-items-component covers the heading and the body
   together. [data-sscart] alone remains the fallback.

   The DIALOG is outside that component too, and it carries state — see the
   note on cart-drawer--empty in paint(). Anything Horizon puts on the dialog
   has to be synced by hand.

   Scroll position is carried across the swap. The old code replaced the cart
   root and let the drawer jump to the top on every change.

   No backslashes and no double quote inside a single-quoted string: these files
   upload through a GraphQL block string, which rejects both. */
(() => {
  if (window.__ssCartInit) return;
  window.__ssCartInit = true;

  const ROOT = '[data-sscart]';
  const HOST = 'cart-items-component';
  const DIALOG = '.cart-drawer__dialog';
  const EMPTY = 'cart-drawer--empty';
  let busy = false;

  const root = () => document.querySelector(ROOT);
  const sectionId = () => {
    const r = root();
    return r ? r.getAttribute('data-sscart-id') : null;
  };

  /* ---------------- painting -------------------------------------------- */
  function paint(html) {
    const current = root();
    if (!current || !html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const oldScroll = current.querySelector('.sscart__dscroll');
    const top = oldScroll ? oldScroll.scrollTop : 0;

    /* Prefer the whole component so the heading count updates with the body.
       Fall back to our own subtree if the markup ever moves. */
    const host = current.closest(HOST);
    const nextHost = doc.querySelector(HOST);
    let landed = null;

    if (host && nextHost) {
      host.replaceWith(nextHost);
      landed = nextHost;
    } else {
      const next = doc.querySelector(ROOT);
      if (!next) return false;
      current.replaceWith(next);
      landed = next;
    }

    const newScroll = landed.querySelector('.sscart__dscroll');
    if (newScroll) newScroll.scrollTop = top;

    /* Horizon stamps cart-drawer--empty on the DIALOG at render time, and
       nothing ever takes it off again. The dialog is the grandparent of the
       component we just swapped, so it survived untouched.

       The failure it caused, seen on mobile: load a page with an empty cart,
       add one item, open the drawer — the class is still there, so
           .cart-drawer--empty .cart-drawer__header{justify-content:right;}
       right-aligns the heading, and Horizon's .close-button is position:fixed
       to the viewport corner, so the X lands on top of "Your bag" and its count
       bubble. The same stale class also pins .cart-drawer__inner to
       height:100dvh with align-items:center.

       The response already carries a freshly rendered dialog. Copy the state
       off it rather than trying to recompute what empty means here. */
    const liveDialog = document.querySelector(DIALOG);
    const nextDialog = doc.querySelector(DIALOG);
    if (liveDialog && nextDialog) {
      liveDialog.classList.toggle(EMPTY, nextDialog.classList.contains(EMPTY));
      const labelled = nextDialog.getAttribute('aria-labelledby');
      if (labelled) liveDialog.setAttribute('aria-labelledby', labelled);
    }

    /* The cart bubble lives elsewhere in the same section, so it comes free
       with the response we already have. The old code spent a whole extra
       request on this. */
    const icon = document.querySelector('cart-icon');
    const nextIcon = doc.querySelector('cart-icon');
    if (icon && nextIcon) icon.replaceWith(nextIcon);

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: null, sourceId: 'ss-cart', data: { didError: false, source: 'ss-cart' } }
    }));
    return true;
  }

  /* Only used if the sections payload is missing or unparseable. */
  async function hardRefresh() {
    const sid = sectionId();
    if (!sid) { window.location.reload(); return; }
    try {
      const res = await fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sid), {
        headers: { Accept: 'text/html' }
      });
      if (!paint(await res.text())) window.location.reload();
    } catch (e) {
      window.location.reload();
    }
  }

  /* ---------------- the one call ---------------------------------------- */
  async function mutate(url, body) {
    const sid = sectionId();
    if (sid) body.sections = sid;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error((json && json.description) || 'cart');
    const html = json && json.sections && sid ? json.sections[sid] : null;
    if (!paint(html)) await hardRefresh();
    return json;
  }

  /* ---------------- optimistic helpers ---------------------------------- */
  function rowFor(el) {
    return el.closest ? el.closest('.sscart__item') : null;
  }

  /* Collapse the row rather than deleting it outright, so the list does not
     snap and so it can be restored if the request fails. */
  function collapse(row) {
    if (!row) return () => {};
    const h = row.offsetHeight;
    row.style.overflow = 'hidden';
    row.style.height = h + 'px';
    row.getBoundingClientRect();
    row.classList.add('is-going');
    row.style.height = '0px';
    row.style.opacity = '0';
    return () => {
      row.classList.remove('is-going');
      row.style.height = '';
      row.style.opacity = '';
      row.style.overflow = '';
    };
  }

  /* ---------------- interactions ---------------------------------------- */
  document.addEventListener('click', async (e) => {
    const t = e.target;
    if (!t || !t.closest) return;

    /* -- remove -- */
    const rm = t.closest('[data-sscart-remove]');
    if (rm) {
      e.preventDefault();
      if (busy) return;
      busy = true;
      const row = rowFor(rm);
      const restore = collapse(row);
      try {
        await mutate('/cart/change.js', { id: rm.getAttribute('data-sscart-remove'), quantity: 0 });
      } catch (err) {
        restore();
        if (typeof window.ssToast === 'function') window.ssToast('Could not remove that.', { type: 'error' });
      }
      busy = false;
      return;
    }

    /* -- quantity -- */
    const q = t.closest('[data-sscart-qty]');
    if (q) {
      e.preventDefault();
      if (busy || q.disabled) return;
      busy = true;
      const to = parseInt(q.getAttribute('data-to'), 10);
      const row = rowFor(q);
      let restore = () => {};

      if (to <= 0) {
        restore = collapse(row);
      } else if (row) {
        /* Paint the new number straight away, and freeze the stepper so a
           double tap cannot race the response. */
        const n = row.querySelector('.sscart__qn');
        if (n) n.textContent = String(to);
        row.querySelectorAll('[data-sscart-qty]').forEach((b) => { b.disabled = true; });
      }

      try {
        await mutate('/cart/change.js', { id: q.getAttribute('data-sscart-qty'), quantity: to });
      } catch (err) {
        restore();
        await hardRefresh();
        if (typeof window.ssToast === 'function') window.ssToast('Could not update that.', { type: 'error' });
      }
      busy = false;
      return;
    }

    /* -- upsell add -- */
    const add = t.closest('[data-sscart-add]');
    if (add) {
      e.preventDefault();
      if (busy) return;
      busy = true;
      const label = add.textContent;
      add.disabled = true;
      add.textContent = 'Adding…';
      try {
        await mutate('/cart/add.js', { items: [{ id: parseInt(add.getAttribute('data-sscart-add'), 10), quantity: 1 }] });
      } catch (err) {
        add.disabled = false;
        add.textContent = label;
        if (typeof window.ssToast === 'function') window.ssToast('Could not add that.', { type: 'error' });
      }
      busy = false;
      return;
    }
  });

  /* Auto-open the side-cart when arriving with ?opencart=1 (the /cart page
     redirects here). */
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get('opencart') === '1') {
      const u = new URL(window.location.href);
      u.searchParams.delete('opencart');
      window.history.replaceState({}, '', u.pathname + (u.search || '') + u.hash);
      let n = 0;
      const iv = setInterval(() => {
        n++;
        const c = document.querySelector('cart-drawer-component');
        if (c) {
          if (typeof c.open === 'function') { try { c.open(); } catch (e) {} }
          const d = c.refs && c.refs.dialog;
          if (d && !d.open && typeof d.showModal === 'function') { try { d.showModal(); } catch (e) {} }
          if (d && d.open) { clearInterval(iv); return; }
        }
        if (n > 60) clearInterval(iv);
      }, 100);
    }
  } catch (e) {}

  /* ---------------- /cart links open the drawer -------------------------
     The /cart route is only a redirect stub, so any in-theme link to it
     cost a blank screen plus a second full page load before the drawer
     appeared. The worst offender was the bag button inside the mobile
     menu (sk-mobile-menu), which is the most-tapped control on the store.

     Intercept here rather than editing each section, so every current and
     future link to /cart behaves the same. The anchors keep their href, so
     with JS off the link still resolves to a real cart page.

     Bails out without preventDefault if the drawer is not on the page, so
     a store with the drawer disabled still navigates normally. */
  function isCartLink(href) {
    if (!href) return false;
    var h = String(href);
    var i = h.indexOf('://');
    if (i > -1) {
      if (h.indexOf(window.location.origin) !== 0) return false;
      h = h.slice(window.location.origin.length);
    }
    if (h.charAt(0) !== '/') return false;
    var q = h.indexOf('?'); if (q > -1) h = h.slice(0, q);
    var f = h.indexOf('#'); if (f > -1) h = h.slice(0, f);
    if (h.length > 1 && h.charAt(h.length - 1) === '/') h = h.slice(0, -1);
    /* matches /cart and a locale-prefixed /xx-yy/cart, never /cart/add */
    return h.length >= 5 && h.slice(-5) === '/cart';
  }

  function openDrawer() {
    var n = 0;
    var iv = setInterval(function () {
      n++;
      var c = document.querySelector('cart-drawer-component');
      if (c) {
        if (typeof c.open === 'function') { try { c.open(); } catch (e) {} }
        var d = c.refs && c.refs.dialog;
        if (d && !d.open && typeof d.showModal === 'function') { try { d.showModal(); } catch (e) {} }
        if (d && d.open) { clearInterval(iv); return; }
      }
      if (n > 20) clearInterval(iv);
    }, 50);
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var t = e.target;
    if (!t || !t.closest) return;
    var a = t.closest('a[href]');
    if (!a) return;
    if (a.target && a.target !== '_self') return;
    if (!isCartLink(a.getAttribute('href'))) return;
    if (!document.querySelector('cart-drawer-component')) return;

    e.preventDefault();

    /* If the tap came from inside the mobile menu, close it first, then open
       the cart once the menu finishes animating out. */
    var inMenu = !!t.closest('[data-skm]');
    if (inMenu && window.__skm && typeof window.__skm.close === 'function') {
      try { window.__skm.close(); } catch (err) {}
      setTimeout(openDrawer, 440);
    } else {
      openDrawer();
    }
  }, true);

})();
